import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { ModelSectionBase } from './model-section-base';
import { t } from '@vitesse/base-ui/i18n';
import { Models } from '@vitesse/proto/vitesse/v1/model_pb.js';
import { button } from '@vitesse/base-ui/components/base-buttons';

/*
 * <vitesse-model-commands> — the model's DECLARED command surface (1:1 with the
 * agent's ModelCommandsSection). Each declared command (model.commands) is a
 * Play button that fires ExecuteCommand. The 5 reserved lifecycle commands are
 * NOT here — Start/Stop live in the page header, like the agent.
 *
 * Extends ModelSectionBase → inherits the section chrome (incl. the
 * .model-commands-section class, so the scoped `.command-buttons` SCSS lays the
 * buttons out horizontally) + the model binding. Droppable as a dashboard widget.
 */
@customElement('vitesse-model-commands')
export class ModelCommandsSection extends ModelSectionBase {
  @state() private busy = false;

  protected sectionHeading(): string {
    return t('model.commands');
  }
  protected sectionClass(): string {
    return 'model-commands-section';
  }

  private async exec(command: string): Promise<void> {
    if (!this.models || this.busy) return;
    this.busy = true;
    try {
      await this.models.execute(this.modelId, command);
    } finally {
      this.busy = false;
    }
  }

  protected renderContent(): unknown {
    const m = this.model;
    if (!m) return nothing;
    if (m.commands.length === 0) {
      return html`<div class="home-empty-hint">${t('model.noCommands')}</div>`;
    }
    // Base buttons (text only — no icon), the app's standard command surface.
    return html`
      <div class="command-buttons">
        <!-- One lock for the row: every button here is the same RPC and the same
             permission, so N locks would repeat one sentence N times. -->
        <vitesse-gated .rpc=${Models.method.executeCommand} inline>
          ${m.commands.map((c) => button({ label: c, color: 'blue', onClick: () => void this.exec(c), disabled: this.busy }))}
        </vitesse-gated>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-model-commands': ModelCommandsSection;
  }
}
